import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Dialog, IconButton, useMediaQuery } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  PlayArrowRounded as PlayIcon,
} from '@mui/icons-material';
import { api, type AcceleratorHotspot, type AcceleratorVideoSource } from '../../services/api';
import huttonsWordmark from '../../../img/Huttons_white.png';
import AcceleratorVideoPlayer from './AcceleratorVideoPlayer';
import { loadAgentCutout, type AgentCutout } from './agentCutout';

interface Props {
  hotspot: AcceleratorHotspot | null;
  onClose: () => void;
}

const GOLD = '#f2c27e';
const GOLD_TEXT = '#f7cf93';
const FONT = '"Poppins", "Archivo", "Helvetica Neue", Arial, sans-serif';
const LOCKUP_FONT = '"Archivo", "Helvetica Neue", Arial, sans-serif';

export function portalPhotoUrl(photo: string | undefined | null): string | undefined {
  if (!photo) return undefined;
  if (/^https?:\/\//i.test(photo)) return photo;
  return `https://portal.huttonsgroup.com/${photo.replace(/^\/+/, '')}`;
}

// Soft out-of-focus lights scattered over the backdrop, as in the video title card.
const BOKEH: Array<{ left: string; top: string; size: number; opacity: number }> = [
  { left: '5%', top: '14%', size: 6, opacity: 0.14 },
  { left: '13%', top: '72%', size: 8, opacity: 0.12 },
  { left: '24%', top: '20%', size: 3, opacity: 0.2 },
  { left: '46%', top: '6%', size: 5, opacity: 0.13 },
  { left: '50%', top: '80%', size: 7, opacity: 0.1 },
  { left: '66%', top: '20%', size: 3, opacity: 0.18 },
  { left: '79%', top: '55%', size: 9, opacity: 0.09 },
  { left: '91%', top: '30%', size: 4, opacity: 0.16 },
  { left: '87%', top: '82%', size: 6, opacity: 0.12 },
];

// Light arcs circling the ring: [start rotation, inset beyond the ring in % of its size].
const ARCS: Array<[number, number]> = [
  [-62, 7],
  [78, 6],
  [168, 9],
];

/** Gold ring thickness and the lavender inner line, as fractions of the ring diameter. */
const GOLD_BAND = 0.026;
const INNER_LINE = 0.012;

/**
 * Where the cut-out photo sits, in fractions of the ring diameter: the head
 * top rises HEAD_RISE above the ring and the photo's lower crop edge hides
 * just inside the bottom of the ring.
 */
const HEAD_RISE = 0.17;
const PHOTO_BOTTOM = 0.97;

function cutoutLayout(cutout: AgentCutout) {
  const height = (PHOTO_BOTTOM + HEAD_RISE) / (1 - cutout.top);
  const width = height * cutout.aspect;
  const top = PHOTO_BOTTOM - height;
  const left = 0.5 - ((cutout.left + cutout.right) / 2) * width;
  return { top, left, width, height };
}

/** Largest font size (cqw) that keeps `text` on one line within `widthCqw`. */
function fitFont(text: string, maxCqw: number, widthCqw: number, emPerChar: number): number {
  return Math.min(maxCqw, widthCqw / Math.max(1, text.length * emPerChar));
}

/**
 * Agent title card opened from a poster area — the same layout as the
 * accelerator video's opening frame (ring portrait left, name block right).
 * Sizes are in container units (cqw) so the card scales as one picture;
 * phones get a stacked version of the same pieces.
 */
export default function AcceleratorProfileDialog({ hotspot: openHotspot, onClose }: Props) {
  const stacked = useMediaQuery('(max-width:600px)');

  // Keep the last agent on screen while the dialog animates closed.
  const [hotspot, setHotspot] = useState<AcceleratorHotspot | null>(openHotspot);
  useEffect(() => {
    if (openHotspot) setHotspot(openHotspot);
  }, [openHotspot]);

  const [video, setVideo] = useState<AcceleratorVideoSource | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  useEffect(() => {
    setVideo(null);
    setVideoError(null);
    setVideoLoading(false);
  }, [openHotspot?.id]);

  const agent = hotspot?.agent;
  const name = (agent?.name || agent?.ceaNumber || '').toUpperCase();
  const designation = agent?.designation || '';
  const photo = portalPhotoUrl(agent?.photo);

  // Cut-out photo (head above the ring). `undefined` = still working, null = use the plain round photo.
  const [cutout, setCutout] = useState<AgentCutout | null | undefined>(undefined);
  useEffect(() => {
    if (!photo) {
      setCutout(null);
      return undefined;
    }
    let cancelled = false;
    setCutout(undefined);
    loadAgentCutout(photo).then((result) => !cancelled && setCutout(result));
    return () => {
      cancelled = true;
    };
  }, [photo]);

  const handleWatch = async () => {
    if (!hotspot) return;
    setVideoLoading(true);
    setVideoError(null);
    try {
      setVideo(await api.getAcceleratorVideo(hotspot.id));
    } catch (error) {
      setVideoError(error instanceof Error ? error.message : 'Could not prepare the video');
    } finally {
      setVideoLoading(false);
    }
  };

  // Sizes in cqw of the card width: desktop numbers come from the 1920px frame.
  const u = (desktop: number, phone: number) => `${stacked ? phone : desktop}cqw`;
  const ring = stacked ? 60 : 29;
  const textWidth = stacked ? 84 : 40;
  const nameSize = fitFont(name, stacked ? 10 : 5.7, textWidth, 0.7);
  const designationSize = fitFont(designation, stacked ? 5 : 2.5, textWidth, 0.53);
  const center = stacked ? '50% 31%' : '31.5% 47.7%';
  const layout = cutout ? cutoutLayout(cutout) : null;

  const iconButtonSx = {
    position: 'absolute' as const,
    top: { xs: 8, sm: 12 },
    zIndex: 3,
    color: '#fff',
    bgcolor: 'rgba(0, 0, 0, 0.28)',
    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.45)' },
  };

  const cutoutImgSx = layout && {
    position: 'absolute' as const,
    top: `${layout.top * 100}%`,
    left: `${layout.left * 100}%`,
    width: `${layout.width * 100}%`,
    height: `${layout.height * 100}%`,
    maxWidth: 'none',
    display: 'block',
    userSelect: 'none' as const,
    pointerEvents: 'none' as const,
  };

  return (
    <Dialog
      open={!!openHotspot}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          m: 0,
          position: 'relative',
          overflow: 'hidden',
          width: stacked ? '92vw' : 'min(94vw, calc((100dvh - 48px) * 16 / 9), 1100px)',
          aspectRatio: stacked && !video ? 'auto' : '16 / 9',
          maxHeight: 'none',
          borderRadius: { xs: 2, sm: 2.5 },
          containerType: 'inline-size',
          color: '#fff',
          fontFamily: FONT,
          bgcolor: '#251266',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.7)',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.7)' } } }}
    >
      {video ? (
        <>
          <IconButton aria-label="Back to profile" size="small" onClick={() => setVideo(null)} sx={{ ...iconButtonSx, left: { xs: 8, sm: 12 } }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="Close" size="small" onClick={onClose} sx={{ ...iconButtonSx, right: { xs: 8, sm: 12 } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box sx={{ width: '100%', height: '100%', '& > div': { borderRadius: 0, height: '100%' } }}>
            <AcceleratorVideoPlayer source={video} />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: stacked ? 'column' : 'row',
            alignItems: 'center',
            pb: stacked ? '9cqw' : 0,
            pt: stacked ? '30cqw' : 0,
            // Violet backdrop: brightest behind the portrait, a magenta glow low left.
            background: [
              `radial-gradient(ellipse 34% 30% at 6% 100%, rgba(214, 120, 255, 0.42), transparent 75%)`,
              `radial-gradient(circle at ${center}, rgba(190, 150, 255, 0.55) 0%, transparent ${stacked ? 45 : 24}%)`,
              stacked
                ? 'radial-gradient(ellipse 95% 55% at 50% 31%, #8a5ae0 0%, #6034c2 34%, #3e1e94 66%, #241264 100%)'
                : 'radial-gradient(ellipse 58% 82% at 31.5% 47.7%, #8a5ae0 0%, #6034c2 30%, #3e1e94 62%, #241264 100%)',
            ].join(', '),
          }}
        >
          {/* Soft light rays fanning out from the portrait */}
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              filter: 'blur(0.25cqw)',
              background: `repeating-conic-gradient(from 3deg at ${center}, rgba(235, 210, 255, 0.16) 0deg 2.4deg, transparent 2.4deg 13deg)`,
              WebkitMaskImage: `radial-gradient(circle at ${center}, #000 10%, rgba(0, 0, 0, 0.5) 34%, transparent 70%)`,
              maskImage: `radial-gradient(circle at ${center}, #000 10%, rgba(0, 0, 0, 0.5) 34%, transparent 70%)`,
            }}
          />
          {BOKEH.map((dot, index) => (
            <Box
              key={index}
              aria-hidden
              sx={{
                position: 'absolute',
                left: dot.left,
                top: dot.top,
                width: `${dot.size}cqw`,
                height: `${dot.size}cqw`,
                borderRadius: '50%',
                bgcolor: `rgba(170, 195, 255, ${dot.opacity})`,
                filter: 'blur(0.5cqw)',
                pointerEvents: 'none',
              }}
            />
          ))}

          <IconButton aria-label="Close" size="small" onClick={onClose} sx={{ ...iconButtonSx, left: { xs: 8, sm: 12 } }}>
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* Huttons lockup, top right */}
          <Box
            sx={{
              position: 'absolute',
              top: stacked ? '4.5cqw' : '4.4%',
              right: stacked ? '4.5cqw' : '2.8%',
              width: u(20.4, 36),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              fontFamily: LOCKUP_FONT,
              fontWeight: 800,
              fontSize: u(1.2, 2.1),
              lineHeight: 1.12,
              letterSpacing: '0.025em',
              color: '#fff',
              whiteSpace: 'nowrap',
            }}
          >
            <Box component="img" src={huttonsWordmark} alt="Huttons" sx={{ width: '100%', display: 'block', mb: u(0.35, 0.6) }} />
            <span>SINGAPORE’S LARGEST</span>
            <span>PRIVATE REAL ESTATE AGENCY</span>
          </Box>

          {/* Portrait in the glowing gold ring */}
          <Box
            sx={{
              position: stacked ? 'relative' : 'absolute',
              left: stacked ? 'auto' : '31.5%',
              top: stacked ? 'auto' : '47.7%',
              transform: stacked ? 'none' : 'translate(-50%, -50%)',
              width: `${ring}cqw`,
              height: `${ring}cqw`,
              flexShrink: 0,
            }}
          >
            {ARCS.map(([start, inset]) => (
              <Box
                key={start}
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: `-${inset}%`,
                  borderRadius: '50%',
                  border: `${ring * 0.009}cqw solid transparent`,
                  borderTopColor: 'rgba(255, 255, 255, 0.95)',
                  filter: `drop-shadow(0 0 ${ring * 0.012}cqw #f3b8ff) drop-shadow(0 0 ${ring * 0.03}cqw #b06cff)`,
                  transform: `rotate(${start}deg)`,
                  animation: 'acceleratorArcSpin 16s linear infinite',
                  '@keyframes acceleratorArcSpin': {
                    from: { rotate: '0deg' },
                    to: { rotate: '360deg' },
                  },
                }}
              />
            ))}

            {/* Gold band with its glow */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'linear-gradient(150deg, #fff0c4 0%, #f3c26a 28%, #dc9c3e 58%, #ffe0a0 100%)',
                boxShadow: `0 0 ${ring * 0.05}cqw rgba(255, 196, 120, 0.6), 0 0 ${ring * 0.16}cqw rgba(196, 120, 255, 0.55)`,
              }}
            />

            {/* Inside of the ring: the photo, clipped to the circle */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                clipPath: `circle(${(0.5 - GOLD_BAND) * 100}% at 50% 50%)`,
                background: 'radial-gradient(circle at 50% 38%, #75639b 0%, #534274 55%, #3b2d58 100%)',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontSize: `${ring * 0.26}cqw`,
                color: 'rgba(255, 255, 255, 0.85)',
              }}
            >
              {layout && cutout ? (
                <Box component="img" src={cutout.url} alt={name} draggable={false} sx={cutoutImgSx} />
              ) : cutout === null && photo ? (
                <Box
                  component="img"
                  src={photo}
                  alt={name}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                />
              ) : cutout === null ? (
                name.substring(0, 2)
              ) : null}
            </Box>

            {/* Lavender line just inside the gold */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: `${GOLD_BAND * 100}%`,
                borderRadius: '50%',
                border: `${ring * INNER_LINE}cqw solid rgba(232, 220, 255, 0.85)`,
                pointerEvents: 'none',
              }}
            />

            {/* Head and shoulders above the ring's midline sit over the ring */}
            {layout && cutout && (
              <Box
                component="img"
                src={cutout.url}
                alt=""
                aria-hidden
                draggable={false}
                sx={{
                  ...cutoutImgSx,
                  clipPath: `inset(0 ${(Math.max(0, layout.left + layout.width - 1) / layout.width) * 100}% ${
                    ((layout.top + layout.height - 0.5) / layout.height) * 100
                  }% ${(Math.max(0, -layout.left) / layout.width) * 100}%)`,
                  filter: 'drop-shadow(0 0.3cqw 0.6cqw rgba(30, 0, 70, 0.35))',
                }}
              />
            )}

            {/* SUPER APP ACCELERATOR plate */}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '92.3%',
                transform: 'translate(-50%, -50%)',
                width: '89%',
                height: `${ring * 0.124}cqw`,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(180deg, #ffe9ae 0%, #f3c56d 45%, #d69c44 100%)',
                border: `${ring * 0.008}cqw solid #9c6a1f`,
                boxShadow: `inset 0 ${ring * 0.006}cqw 0 rgba(255, 255, 255, 0.7), 0 ${ring * 0.012}cqw ${ring * 0.03}cqw rgba(0, 0, 0, 0.45)`,
                color: '#2a2140',
                fontWeight: 700,
                fontSize: `${ring * 0.05}cqw`,
                letterSpacing: '0.09em',
                whiteSpace: 'nowrap',
              }}
            >
              SUPER APP ACCELERATOR
            </Box>
          </Box>

          {/* Name block */}
          <Box
            sx={{
              position: stacked ? 'relative' : 'absolute',
              left: stacked ? 'auto' : '55.9%',
              top: stacked ? 'auto' : '34.6%',
              mt: stacked ? '13cqw' : 0,
              width: `${textWidth}cqw`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: stacked ? 'center' : 'flex-start',
              textAlign: stacked ? 'center' : 'left',
              whiteSpace: 'nowrap',
            }}
          >
            <Box sx={{ color: GOLD_TEXT, fontWeight: 500, fontSize: u(1.55, 3.6), letterSpacing: '0.24em', lineHeight: 1 }}>
              SUPER APP ACCELERATOR
            </Box>
            <Box sx={{ mt: u(1.75, 3), width: stacked ? '100%' : '38.2cqw', height: u(0.2, 0.5), bgcolor: GOLD, borderRadius: 1 }} />
            <Box
              sx={{
                mt: u(1.7, 3.5),
                color: '#fff',
                fontWeight: 700,
                fontSize: `${nameSize}cqw`,
                lineHeight: 1.15,
                letterSpacing: '0.01em',
              }}
            >
              {name}
            </Box>
            {designation && (
              <Box sx={{ mt: u(2.6, 3), color: GOLD_TEXT, fontWeight: 500, fontSize: `${designationSize}cqw`, lineHeight: 1.2 }}>
                {designation}
              </Box>
            )}
            <Box
              sx={{
                mt: u(4.4, 4.5),
                color: 'rgba(255, 255, 255, 0.78)',
                fontWeight: 500,
                fontSize: u(1.15, 2.7),
                letterSpacing: '0.1em',
                lineHeight: 1.3,
              }}
            >
              HUTTONS SUPER APP&nbsp;&nbsp;-&nbsp;&nbsp;WHERE GROWTH HAPPENS!
            </Box>

            {hotspot?.hasVideo ? (
              <Box
                component="button"
                type="button"
                onClick={handleWatch}
                disabled={videoLoading}
                sx={{
                  mt: u(3, 6),
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: u(0.7, 1.6),
                  px: u(2, 5),
                  py: u(0.8, 2.2),
                  border: 'none',
                  borderRadius: 999,
                  cursor: videoLoading ? 'default' : 'pointer',
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: u(1.2, 3.6),
                  letterSpacing: '0.08em',
                  color: '#2a1a00',
                  background: 'linear-gradient(90deg, #ffe3a3, #f4c46a 45%, #e3a03e)',
                  boxShadow: '0 0.6cqw 2cqw rgba(0, 0, 0, 0.35), 0 0 1.6cqw rgba(246, 196, 83, 0.45)',
                  transition: 'transform 0.15s, filter 0.15s',
                  '&:hover:not(:disabled)': { transform: 'translateY(-1px)', filter: 'brightness(1.06)' },
                  '&:disabled': { opacity: 0.75 },
                }}
              >
                {videoLoading ? (
                  <CircularProgress size="1em" sx={{ color: '#2a1a00' }} />
                ) : (
                  <PlayIcon sx={{ fontSize: '1.5em', m: '-0.25em' }} />
                )}
                WATCH MY VIDEO
              </Box>
            ) : (
              <Box sx={{ mt: u(3, 6), color: 'rgba(255, 255, 255, 0.6)', fontSize: u(1.05, 3.2), letterSpacing: '0.1em' }}>
                VIDEO COMING SOON
              </Box>
            )}
            {videoError && (
              <Box sx={{ mt: u(1, 2.5), color: '#ffb4a8', fontSize: u(1.05, 3.2), whiteSpace: 'normal' }}>{videoError}</Box>
            )}
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
